import React, { useState } from 'react';

function formatDate(dateStr) {
  if (!dateStr) { return null; }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

function formatTime(dateStr) {
  if (!dateStr) { return null; }
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function EventDetails({ event }) {
  if (!event) { return null; }
  const startDate = event.startsAt ? formatDate(event.startsAt) : null;
  const startTime = event.startsAt ? formatTime(event.startsAt) : null;
  const endTime = event.endsAt ? formatTime(event.endsAt) : null;

  return (
    <div className="mb-6 space-y-2">
      {event.name && (
        <div className="flex items-start gap-2">
          <span className="text-gray-400 mt-0.5" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </span>
          <div>
            <div className="font-medium text-gray-900">{event.name}</div>
            {startDate && (
              <div className="text-sm text-gray-600">
                {startDate}
                {startTime && <> &middot; {startTime}</>}
                {endTime && <> &ndash; {endTime}</>}
              </div>
            )}
          </div>
        </div>
      )}

      {(event.locationName || event.locationAddress) && (
        <div className="flex items-start gap-2">
          <span className="text-gray-400 mt-0.5" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </span>
          <div>
            {event.locationName && <div className="text-gray-900">{event.locationName}</div>}
            {event.locationAddress && <div className="text-sm text-gray-600">{event.locationAddress}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// eslint-disable-next-line security/detect-unsafe-regex
const EMAIL_RE = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

function WalkInForm({ onSubmit, buttonLabel }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!firstName.trim()) {
      setValidationError('First name is required');
      return;
    }
    if (!email.trim() || !EMAIL_RE.test(email.trim())) {
      setValidationError('A valid email is required');
      return;
    }
    setValidationError(null);
    setSubmitting(true);
    await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim() || undefined,
      email: email.trim().toLowerCase(),
      phone: phone.trim() || undefined,
      company: company.trim() || undefined,
    });
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {validationError && (
        <div role="alert" className="p-3 bg-red-50 text-red-700 rounded-md text-sm">{validationError}</div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
          <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)}
            className={inputClass} placeholder="Jane" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
          <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)}
            className={inputClass} placeholder="Doe" />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          className={inputClass} placeholder="jane@example.com" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          className={inputClass} placeholder="(555) 555-1234" />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
        <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
          className={inputClass} placeholder="Acme Corp" />
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="w-full min-h-[44px] px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {submitting ? 'Submitting...' : (buttonLabel || 'Register')}
      </button>
    </form>
  );
}

export default function EventActionView({ event, options, questionText, intentType, onSelect }) {
  const [submitting, setSubmitting] = useState(null);

  const sortedOptions = [...(options || [])].sort((a, b) => a.displayOrder - b.displayOrder);

  async function handleClick(responseValue) {
    setSubmitting(responseValue);
    await onSelect(responseValue);
  }

  async function handleWalkInSubmit(formData) {
    const responseValue = sortedOptions[0]?.responseValue || 'walk_in';
    await onSelect(responseValue, formData);
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-8">
      {questionText && (
        <h1 className="text-xl font-semibold text-gray-900 mb-4 text-center">{questionText}</h1>
      )}

      <EventDetails event={event} />

      {intentType === 'walk_in' ? (
        <WalkInForm
          onSubmit={handleWalkInSubmit}
          buttonLabel={sortedOptions[0]?.buttonLabel}
        />
      ) : (
        <div className="space-y-3">
          {sortedOptions.map((option) => (
            <button
              key={option.responseValue}
              onClick={() => handleClick(option.responseValue)}
              disabled={submitting !== null}
              className="w-full min-h-[44px] px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting === option.responseValue ? 'Submitting...' : option.buttonLabel}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
